import {AxiosInstance} from 'axios'
import MyAxiosInstanceAcademy from './axiosInstanceAcademy'

// Service do módulo Trilha do Guru. Consome o academy-service — a baseURL
// (VITE_ACADEMY_URL) já contém o prefixo /api/academy/v1
// (local: http://localhost:8085/api/academy/v1).
//
// Endpoints (contrato F2):
//   GET  /progresso                 -> ProgressoServidor (zeros se não existe)
//   POST /licoes/concluir           -> ResultadoLicaoServidor (idempotente por request_id)
//   POST /revisoes/concluir         -> ResultadoLicaoServidor (idempotente por request_id)
//   PUT  /progresso/importar        -> 200 na 1ª vez; 409 se o usuário já tem progresso
//   GET  /admin/estatisticas        -> agregados da organização (Security scope admin)
//   GET  /admin/usuarios            -> página de servidores da org (Security scope admin)

export interface StreakServidor {
    atual: number
    recorde: number
    ultimo_dia: string | null
}

// Contadores de uma pílula: `t` tentativas, `e` erros EM ABERTO (acertar de
// primeira na revisão redime — zera `e`). O histórico cumulativo de erros é
// outra coisa e vem dos agregados do admin, que redenção nenhuma apaga.
export interface StatsPilulaServidor {
    t: number
    e: number
}

export interface ProgressoServidor {
    xp: number
    concluidas: Record<string, boolean[]>
    dominadas: string[]
    streak: StreakServidor
    tamanhos: Record<string, number>
    // Ausente em servidor anterior à migração 0004 — nesse caso o cache local
    // segue dono dos stats (ver servidorParaLocal).
    stats?: Record<string, StatsPilulaServidor>
}

export interface ResultadoPilulaServidor {
    pilula_id: string
    primeira: boolean
}

export interface ConcluirLicaoPayload {
    request_id: string
    secao_slug: string
    licao: number
    tamanho_secao: number
    resultados: ResultadoPilulaServidor[]
}

// Sessão de REVISÃO: pílulas que a pessoa já errou, possivelmente de seções
// diferentes. Por isso NÃO carrega `secao_slug` nem `tamanho_secao` — são eles
// que disparam a reconciliação da seção no servidor, que resetaria o progresso
// de uma seção inteira se a revisão os mandasse. O servidor soma XP pela regra
// normal com primeira_conclusao=False, registra as respostas nas estatísticas
// e atualiza a sequência de dias, mas não toca em `concluidas`.
export interface ConcluirRevisaoPayload {
    request_id: string
    resultados: ResultadoPilulaServidor[]
}

// Mesmo shape para lição e revisão: as duas devolvem o estado autoritativo que
// sobrescreve o cache local.
export interface ResultadoLicaoServidor {
    xp_ganho: number
    xp_total: number
    dominadas_novas: number
    dominadas_total: number
    streak: number
    recorde: number
    novo_recorde: boolean
}

export interface PilulaErradaAcademy {
    pilula_id: string
    erros: number
    respostas: number
}

// Erros agregados por SEÇÃO — o recorte que vira decisão de treinamento
// ("qual assunto a organização não domina"), enquanto pilulas_mais_erradas
// aponta a questão individual. Conta APENAS respostas de lição: as respostas
// de revisão não têm seção a declarar (misturam seções) e ficam de fora deste
// recorte, mas seguem contando em taxa_erro_global e pilulas_mais_erradas.
export interface SecaoErradaAcademy {
    secao_slug: string
    erros: number
    respostas: number
}

export interface EstatisticasAcademy {
    usuarios_total: number
    // Três janelas de atividade, fechadas em hoje (America/Sao_Paulo) e
    // inclusivas: 7d = hoje + 6 dias anteriores; 30d = hoje + 29 anteriores.
    // "Ativos hoje" sozinho não distingue produto que pegou de dia bom.
    ativos_hoje: number
    ativos_7d: number
    ativos_30d: number
    xp_medio: number
    // Sempre com TODAS as chaves, inclusive zeradas — o gráfico não muda de
    // forma entre uma leitura e outra.
    distribuicao_titulos: Record<string, number>
    // Faixas de sequência VIGENTE ("1-2", "3-6", "7-13", "14-29", "30+"). Quem
    // está com a sequência quebrada não entra em faixa nenhuma, então a soma
    // pode ser MENOR que usuarios_total.
    distribuicao_streak: Record<string, number>
    respostas_total: number
    taxa_erro_global: number
    pilulas_mais_erradas: PilulaErradaAcademy[]
    secoes_mais_erradas: SecaoErradaAcademy[]
    // Separadas por origem: uma revisão não é uma lição do mapa e não pode
    // inflar o número de lições concluídas da organização.
    licoes_concluidas_total: number
    revisoes_concluidas_total: number
}

// Uma linha do acompanhamento por servidor.
export interface UsuarioAdminAcademy {
    user_id: string
    // NULOS para quem não escreveu progresso nenhum desde a migração 0003 — o
    // dado vem do JWT e só é espelhado numa conclusão (lição/revisão) ou na
    // importação do F1 — e para tokens sem esses claims. O painel cai para
    // user_id em vez de inventar rótulo.
    nome: string | null
    email: string | null
    xp: number
    // Nome do título da régua, derivado do xp da linha.
    titulo: string
    dominadas_total: number
    // Sequência VIGENTE (0 se quebrou). recorde e ultimo_dia são histórico cru.
    streak_atual: number
    streak_recorde: number
    ultimo_dia: string | null
}

// `total` é a contagem completa da organização (não da página), para o painel
// paginar sem adivinhar se há próxima página.
export interface UsuariosAdminAcademy {
    total: number
    limite: number
    deslocamento: number
    itens: UsuarioAdminAcademy[]
}

// ---- Visão GLOBAL do admin master (escopo academy:master) ----
// Mesmos números do painel da org, agregados POR organização: a tela do
// master compara clientes, não pessoas — o detalhe individual é drill-down.
export interface OrganizacaoResumoAcademy {
    organization_id: string
    usuarios_total: number
    ativos_hoje: number
    ativos_7d: number
    ativos_30d: number
    xp_medio: number
}

export interface OrganizacoesAcademy {
    organizacoes: OrganizacaoResumoAcademy[]
}

// A linha cross-org é a da org MAIS a organização: dois homônimos de
// clientes diferentes precisam ser distinguíveis na tela do master.
export interface UsuarioGlobalAcademy extends UsuarioAdminAcademy {
    organization_id: string
}

export interface UsuariosGlobaisAcademy {
    total: number
    limite: number
    deslocamento: number
    itens: UsuarioGlobalAcademy[]
}

// Teto do servidor: pedir a organização inteira numa resposta só não é opção
// do cliente (acima disto o endpoint devolve 422).
export const USUARIOS_LIMITE_MAX = 200

export class AcademyApiService {
    private api: AxiosInstance

    constructor() {
        this.api = MyAxiosInstanceAcademy
    }

    async getProgresso(): Promise<ProgressoServidor> {
        const {data} = await this.api.get('/progresso')
        return data
    }

    async concluirLicao(
        payload: ConcluirLicaoPayload
    ): Promise<ResultadoLicaoServidor> {
        const {data} = await this.api.post('/licoes/concluir', payload)
        return data
    }

    async concluirRevisao(
        payload: ConcluirRevisaoPayload
    ): Promise<ResultadoLicaoServidor> {
        const {data} = await this.api.post('/revisoes/concluir', payload)
        return data
    }

    // Migração única do progresso local (F1) para o servidor. 409 = usuário
    // já tem progresso no banco (ignorar).
    async importarProgresso(doc: {
        xp: number
        concluidas: Record<string, boolean[]>
        dominadas: string[]
        streak: StreakServidor
        tamanhos: Record<string, number>
        stats: Record<string, StatsPilulaServidor>
    }): Promise<void> {
        await this.api.put('/progresso/importar', doc)
    }

    async getEstatisticasAdmin(): Promise<EstatisticasAcademy> {
        const {data} = await this.api.get('/admin/estatisticas')
        return data
    }

    // Página do acompanhamento por servidor. Ordem do servidor: xp DESC,
    // user_id ASC (o desempate é o que impede a paginação de "tremer").
    // `limite` acima de USUARIOS_LIMITE_MAX e `deslocamento` negativo são 422.
    async getUsuariosAdmin(
        limite = 50,
        deslocamento = 0
    ): Promise<UsuariosAdminAcademy> {
        const {data} = await this.api.get('/admin/usuarios', {
            params: {limite, deslocamento},
        })
        return data
    }

    // As duas rotas abaixo exigem o escopo `academy:master` no JWT — para
    // qualquer outro token o serviço responde 403, e o painel só as chama
    // quando `papelAdmin()` é 'master'. Gate real no servidor; o front só
    // evita a chamada fadada ao erro.
    async getOrganizacoesGlobais(): Promise<OrganizacoesAcademy> {
        const {data} = await this.api.get('/admin/global/organizacoes')
        return data
    }

    async getUsuariosGlobais(
        organizacao: string | null,
        limite = 50,
        deslocamento = 0
    ): Promise<UsuariosGlobaisAcademy> {
        const {data} = await this.api.get('/admin/global/usuarios', {
            params: {
                ...(organizacao ? {organizacao} : {}),
                limite,
                deslocamento,
            },
        })
        return data
    }
}
